import { z } from 'zod';

/**
 * A date step can be booked for several days at once (issue #32): a two-day
 * shoot is two events on the same workflow step, which is exactly what the
 * backend's `dates` array and `assertEventDateOrder` already expect.
 */
const dateStepSchema = z.object({
  type: z.enum(['SCOUTING', 'SHOOTING']),
  enabled: z.boolean(),
  dates: z.array(z.object({ value: z.string() })),
});

/** Date steps run strictly one after another; the backend enforces the same order. */
const SCHEDULING_RANK = { SCOUTING: 0, SHOOTING: 1 } as const;
const SCHEDULING_LABEL = {
  SCOUTING: 'Izviđanje',
  SHOOTING: 'Snimanje',
} as const;

/** The reservation form. A PM may book themselves with no note (issue #41). */
export function makeReservationSchema() {
  return z
    .object({
      showId: z.string().min(1, 'Izaberite emisiju'),
      title: z.string().min(1, 'Naziv epizode je obavezan'),
      dateSteps: z.array(dateStepSchema),
      pmId: z.string().optional(),
      notifyRoleIds: z.array(z.string()).optional(),
      location: z.string().optional(),
      venue: z.string().optional(),
      // Optional, but when present it has to be a real link, or the email
      // would carry a dead anchor.
      mapsUrl: z
        .union([
          z.literal(''),
          z.url({ message: 'Google Maps link mora počinjati sa http:// ili https://' }),
        ])
        .optional(),
      note: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const enabled = data.dateSteps.filter((step) => step.enabled);

      if (enabled.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['dateSteps'],
          message: 'Izaberite bar jedan datumski korak',
        });
      }

      data.dateSteps.forEach((step, index) => {
        if (!step.enabled) return;

        const seen = new Set<string>();
        step.dates.forEach((entry, dateIndex) => {
          if (!entry.value) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['dateSteps', index, 'dates', dateIndex, 'value'],
              message: 'Datum je obavezan',
            });
            return;
          }
          // The backend refuses the same (type, date) twice; saying so inline
          // beats a 400 after the episode row already exists.
          if (seen.has(entry.value)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['dateSteps', index, 'dates', dateIndex, 'value'],
              // Phrased around the date, not the step: the step labels differ
              // in grammatical gender and no single verb form fits all four.
              message: `Datum ${entry.value} je već upisan za korak ${SCHEDULING_LABEL[step.type]}`,
            });
          }
          seen.add(entry.value);
        });
      });

      // Snimanje cannot be planned before its own Izviđanje: the calendar is
      // what the coordinator plans against, so the form refuses it inline
      // rather than letting the backend 400 after the fact. Compared on the
      // EARLIEST date per step, mirroring assertEventDateOrder, so a multi-day
      // shoot may still overlap a later step.
      const dated = data.dateSteps
        .map((step, index) => ({
          type: step.type,
          index,
          earliest: step.enabled
            ? step.dates
                .map((entry) => entry.value)
                .filter(Boolean)
                .sort()[0]
            : undefined,
        }))
        .filter((step): step is typeof step & { earliest: string } => !!step.earliest)
        .sort((a, b) => SCHEDULING_RANK[a.type] - SCHEDULING_RANK[b.type]);

      for (let i = 1; i < dated.length; i++) {
        const prev = dated[i - 1];
        const current = dated[i];
        if (current.earliest < prev.earliest) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['dateSteps', current.index, 'dates', 0, 'value'],
            message: `${SCHEDULING_LABEL[current.type]} ne može biti pre koraka ${SCHEDULING_LABEL[prev.type]} (${prev.earliest})`,
          });
        }
      }
    });
}

export type ReservationFormData = z.infer<ReturnType<typeof makeReservationSchema>>;

export const cancelEventSchema = z.object({
  cancelReason: z.string().min(1, 'Razlog otkazivanja je obavezan'),
});

export type CancelEventFormData = z.infer<typeof cancelEventSchema>;

export const externalCrewSchema = z.object({
  roleId: z.string().min(1, 'Izaberite ulogu'),
  externalName: z.string().min(1, 'Ime i prezime je obavezno'),
  externalPhone: z.string().optional(),
  externalEmail: z.string().email('Neispravna email adresa').optional().or(z.literal('')),
});

export type ExternalCrewFormData = z.infer<typeof externalCrewSchema>;

export const vehicleSchema = z.object({
  name: z.string().min(1, 'Naziv vozila je obavezan'),
  note: z.string().optional(),
});

export type VehicleFormData = z.infer<typeof vehicleSchema>;
